import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { WarningFilled } from "@ant-design/icons";
import { App, Button, Form, Input, Modal, theme } from "antd";
import { observer } from "mobx-react";
import { useState } from "react";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useStores } from "../../../../../../store/stores.js";
import { COLOR_ERROR_SOFT } from "../../../../../../style/antdTheme.js";
import { __ } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
import { useAccountV2ErrorHandling } from "../../../../hooks/useAccountV2ErrorHandling.js";
import { InfoCard } from "../../../infoCard.js";
const LicenseActivationRevokeConfirm = observer(({ license, activation, onClose })=>{
    const { token } = theme.useToken();
    const { message } = App.useApp();
    const { accountV2Store } = useStores();
    const [isBusy, setIsBusy] = useState(false);
    const handleRealCommerceError = useAccountV2ErrorHandling();
    const isProduction = activation.data.type === "production";
    const isLost = activation.isConnectionLost;
    const onConfirm = async ()=>{
        setIsBusy(true);
        try {
            await accountV2Store.revokeActivation(license, activation);
            message.success(__("Activation revoked."));
            onClose();
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        } finally{
            setIsBusy(false);
        }
    };
    let consequenceLeftBorder;
    let consequenceIcon;
    let consequenceNode;
    if (isLost) {
        consequenceLeftBorder = token.colorWarning;
        consequenceIcon = /*#__PURE__*/ _jsx(WarningFilled, {
            style: {
                color: token.colorWarning,
                marginRight: 8
            }
        });
        consequenceNode = __("This website hasn't been reachable for a while - the plugin was likely uninstalled. But if it's still live, after revoking it will no longer receive updates and can no longer load content from our cloud.");
    } else {
        consequenceLeftBorder = COLOR_ERROR_SOFT;
        consequenceIcon = /*#__PURE__*/ _jsx(WarningFilled, {
            style: {
                color: COLOR_ERROR_SOFT,
                marginRight: 8
            }
        });
        consequenceNode = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx("strong", {
                    children: __("This website is using the plugin right now.")
                }),
                " ",
                __("After revoking the plugin there will no longer receive updates and can no longer load content from our cloud.")
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(Modal, {
        open: true,
        title: __("Revoke activation"),
        footer: null,
        onCancel: isBusy ? undefined : onClose,
        maskClosable: !isBusy,
        width: 640,
        children: [
            /*#__PURE__*/ _jsx(Form, {
                layout: "vertical",
                disabled: isBusy,
                className: "rc-account-v2-license-form",
                children: /*#__PURE__*/ _jsx(Form.Item, {
                    label: /*#__PURE__*/ _jsxs("span", {
                        style: {
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8
                        },
                        children: [
                            __("Website"),
                            /*#__PURE__*/ _jsx("span", {
                                className: `rc-account-v2-subscription-card__activation-type rc-account-v2-subscription-card__activation-type--${isProduction ? "production" : "development"}`,
                                children: isProduction ? __("Production") : __("Development")
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx(Input, {
                        value: activation.host ?? "",
                        disabled: true,
                        ...MATOMO_MASK
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                style: {
                    marginBottom: 16
                },
                children: /*#__PURE__*/ _jsxs(InfoCard, {
                    variant: "soft-trust",
                    leftBorderColor: consequenceLeftBorder,
                    children: [
                        consequenceIcon,
                        consequenceNode
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-license-modal-footer rc-account-v2-license-modal-footer--paired",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        onClick: onClose,
                        disabled: isBusy,
                        children: __("Keep activation")
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        danger: true,
                        type: "primary",
                        loading: isBusy,
                        onClick: onConfirm,
                        children: __("Revoke activation")
                    })
                ]
            })
        ]
    });
});
export { LicenseActivationRevokeConfirm };
